import type { ReactNode } from 'react';
import { useResourceKit } from '@resource-kit/core/react';
import { ListToolbar, type ListLayoutToggle } from './ListToolbar';
import type { ColumnVisibilityHandle } from './useColumnVisibility';
import type { ColumnSchema, Condition } from '@resource-kit/model';
import { CONDITION_MERGE, type ConditionMergeNotice } from '@resource-kit/controllers';

/**
 * The capability subset `SearchView` reads. Deliberately structural rather than
 * `ResolvedListCapabilities`: the search surface has no business knowing about
 * `select` or `rowActions`, and a narrower prop is a narrower blast radius.
 */
export interface SearchViewCapabilities {
  /** Offer the free-text search box. */
  search: boolean;
  /** Offer the structured filter builder. */
  filter: boolean;
}

export interface SearchViewProps {
  capabilities: SearchViewCapabilities;
  /** Quick-filter presets rendered on the compact top row. */
  presetSlot?: ReactNode;
  searchPlaceholder?: string;
  /** The COMMITTED search value (the one that drives the request). */
  search: string;
  onSearchChange?: (search: string) => void;
  /** Filter-builder schema; `null` (or `capabilities.filter === false`)
   *  renders no builder. */
  schema: ColumnSchema[] | null;
  conditions: Condition[];
  onConditionsChange: (conditions: Condition[]) => void;
  /**
   * Fields whose filter the backend whitelist rejected. Non-empty means the
   * list below is NOT filtered the way the UI claims, so it is announced.
   */
  droppedFilters?: readonly string[];
  /**
   * Same-field condition collapses (design §5.9). Announced for the same
   * reason: a filter that quietly stopped applying is worse than no filter.
   */
  mergeNotices?: readonly ConditionMergeNotice[];
  visibility: ColumnVisibilityHandle;
  onExportSelected: () => void;
  exportSelectedDisabled: boolean;
  onPrint?: (scope: 'all' | 'selected') => void;
  printSelectedDisabled?: boolean;
  onExportAll?: () => void;
  exportAllDisabled?: boolean;
  /** The table/cards switch (#2680); absent ⇒ no switch (a phone). */
  layoutToggle?: ListLayoutToggle;
}

const WARNING_CLASS =
  'mb-3 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-foreground';

/** Dedupe + join a notice group's fields for the warning copy. */
function fieldsOf(notices: readonly ConditionMergeNotice[]): string {
  return [...new Set(notices.map((n) => n.field))].join(', ');
}

/**
 * The list's search / filter surface (#1803).
 *
 * Extracted out of `ListView` so the same surface serves all three data sources
 * — `remote`, `bulk` and `local` — and so capability gating lives in ONE place.
 * Capabilities are opt-in per item and do NOT change this component's contract:
 * a list "with search" and a list "without search" are the same type with a
 * different boolean, not two view types. (A view type per capability
 * combination is a 2ⁿ explosion and was rejected.)
 *
 * It also owns the two "what you see is not what was applied" warnings. Both
 * exist for the same reason: a filter the operator set that did not reach the
 * data must never read as a filtered view.
 */
export function SearchView({
  capabilities,
  presetSlot,
  searchPlaceholder,
  search,
  onSearchChange,
  schema,
  conditions,
  onConditionsChange,
  droppedFilters,
  mergeNotices,
  visibility,
  onExportSelected,
  exportSelectedDisabled,
  onPrint,
  printSelectedDisabled,
  onExportAll,
  exportAllDisabled,
  layoutToggle,
}: SearchViewProps) {
  const { t } = useResourceKit();

  const dropped = droppedFilters && droppedFilters.length > 0 ? droppedFilters : null;

  const merged = (mergeNotices ?? []).filter((n) => n.kind !== CONDITION_MERGE.lastWriteWins);
  const overridden = (mergeNotices ?? []).filter((n) => n.kind === CONDITION_MERGE.lastWriteWins);

  return (
    <div data-testid="search-view">
      {dropped && (
        <div data-testid="filters-dropped-warning" role="alert" className={WARNING_CLASS}>
          {t('rk.resource.filtersDropped', { fields: dropped.join(', ') })}
        </div>
      )}
      {(merged.length > 0 || overridden.length > 0) && (
        <div data-testid="conditions-merged-warning" role="alert" className={WARNING_CLASS}>
          {merged.length > 0 && (
            <div>{t('rk.resource.conditionsMerged', { fields: fieldsOf(merged) })}</div>
          )}
          {overridden.length > 0 && (
            <div>{t('rk.resource.conditionsOverridden', { fields: fieldsOf(overridden) })}</div>
          )}
        </div>
      )}
      <ListToolbar
        presetSlot={presetSlot}
        searchable={capabilities.search}
        searchPlaceholder={searchPlaceholder}
        search={search}
        onSearchChange={onSearchChange}
        schema={capabilities.filter ? schema : null}
        conditions={conditions}
        onConditionsChange={onConditionsChange}
        visibility={visibility}
        onExportSelected={onExportSelected}
        exportSelectedDisabled={exportSelectedDisabled}
        onPrint={onPrint}
        printSelectedDisabled={printSelectedDisabled}
        onExportAll={onExportAll}
        exportAllDisabled={exportAllDisabled}
        layoutToggle={layoutToggle}
      />
    </div>
  );
}
